import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { plainConfirmMessage, usePreviewConfirm } from "@/lib/confirmFlow";
import { useIsAdmin } from "@/lib/session";
import { useToast } from "@/components/Toaster";
import { Facts, Problem, ServerSays } from "@/components/PreviewBits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

// Go-live switches without typing JSON (Friday run sheet steps 15, 22-25). Every change goes through settings_set,
// which says what it does and asks to confirm (admins only; the server refuses anyone else).

interface Setting {
  key: string;
  value: unknown;
}

type Mode = "off" | "preview" | "live";

export const ONOFF: { key: string; label: string; what: string; step: string }[] = [
  { key: "billing.runs_enabled", label: "Automatic renewal charges", what: "The 7 AM, 1 PM and 7 PM runs charge every renewal and installment that is due.", step: "Step 22" },
  {
    key: "keap.course_grants_daily",
    label: "Daily course access for Keap buyers",
    what: "Every morning at 5:45 it gives people who bought a course in Keap their course access, quietly (no emails).",
    step: "Step 23",
  },
  { key: "ga4.enabled", label: "Purchases to Google Analytics", what: "Sends each CRM purchase to Google Analytics (GA4).", step: "Step 25" },
];

export const EMAIL_SWITCHES: { key: string; label: string; what: string }[] = [
  { key: "billing.customer_emails", label: "Billing emails to customers", what: "Receipts, payment-failed reminders and the other billing emails." },
  { key: "billing.renewal_notices", label: "Upcoming-renewal notices", what: "The heads-up before a membership renews. Sent only when billing emails are live too." },
  { key: "billing.card_expiry_notices", label: "Card-expiring notices", what: "Asks members to update a card that expires soon. Sent only when billing emails are live too." },
  { key: "billing.ended_emails", label: "Membership-ended emails", what: "Sent when a membership ends. Sent only when billing emails are live too." },
  { key: "billing.cart_reminders", label: "Abandoned-checkout reminders", what: "Reminders to people who started a checkout and didn't finish. Until set, they follow billing emails." },
];

export const MODE_LABEL: Record<Mode, string> = {
  off: "Off: nothing is sent",
  preview: "Preview: copies to the preview address only",
  live: "Live: sent to customers",
};

/** A preview/live switch as the billing module reads it: only exactly "live" or "off" count; anything else is preview. */
export function readMode(v: unknown): Mode {
  return v === "live" || v === "off" ? v : "preview";
}

export interface HealthCheck {
  enabled?: boolean;
  stripe_payment_method_id?: number;
  authorize_net_payment_method_id?: number;
  payflow_payment_method_id?: number;
}

/** The health check setting with one card chosen (the other gateways' cards are kept). */
export function healthCheckValue(cur: HealthCheck | undefined, enabled: boolean, card: { id: number; gateway: string } | null): HealthCheck {
  const next: HealthCheck = { ...(cur ?? {}), enabled };
  if (card?.gateway === "stripe") next.stripe_payment_method_id = card.id;
  if (card?.gateway === "authorize_net") next.authorize_net_payment_method_id = card.id;
  return next;
}

function settingValue(rows: Setting[] | undefined, key: string): { set: boolean; value: unknown } {
  const r = rows?.find((s) => s.key === key);
  return r ? { set: true, value: r.value } : { set: false, value: undefined };
}

/** Settings → CRM settings: the go-live switches, each with what it does, changed with a confirm. */
export function FridaySwitches() {
  const isAdmin = useIsAdmin();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api<{ settings: Setting[] }>("/api/v1/settings") });
  const [change, setChange] = useState<{ settingKey: string; label: string; value: unknown; describe: string } | null>(null);
  const [health, setHealth] = useState(false);
  const rows = q.data?.settings;
  const hc = settingValue(rows, "billing.health_check").value as HealthCheck | undefined;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Go-live switches</CardTitle>
        <span className="text-xs text-[var(--color-muted-foreground)]">Each change says what it does and asks before it happens</span>
      </CardHeader>
      {!isAdmin && <p className="px-4 text-xs text-[var(--color-warning-fg)]">Only an admin can change these. You can look.</p>}
      {q.isLoading && <Skeleton className="m-3 h-32" />}
      {q.error && <p className="px-4 py-2 text-sm text-[var(--color-danger-fg)]">Couldn't load the settings: {q.error.message}</p>}
      {q.data && (
        <div className="divide-y text-sm">
          {ONOFF.map((s) => {
            const v = settingValue(rows, s.key);
            const on = v.value === true;
            return (
              <Row key={s.key} label={s.label} what={s.what} hint={`${s.step}, setting ${s.key}`}>
                <Badge variant={on ? "success" : "muted"}>{on ? "On" : v.set ? "Off" : "Off (not set)"}</Badge>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setChange({ settingKey: s.key, label: s.label, value: !on, describe: on ? "Off" : "On" })}>
                    Turn {on ? "off" : "on"}
                  </Button>
                )}
              </Row>
            );
          })}
          <Row label="Monthly $1 card check" what="On the 1st of each month at 9 AM it charges $1 to your own card on each gateway and voids it at once, to prove payments work." hint="Step 24, setting billing.health_check">
            <Badge variant={hc?.enabled ? "success" : "muted"}>
              {hc?.enabled ? `On${hc.stripe_payment_method_id ? `, Stripe card #${hc.stripe_payment_method_id}` : ""}` : "Off"}
            </Badge>
            {isAdmin && (
              <Button size="sm" variant="outline" onClick={() => setHealth(true)}>
                Change
              </Button>
            )}
          </Row>
          {EMAIL_SWITCHES.map((s) => {
            const v = settingValue(rows, s.key);
            const mode = readMode(v.value);
            const followsMain = s.key === "billing.cart_reminders" && !v.set;
            return (
              <Row key={s.key} label={s.label} what={s.what} hint={`Step 15, setting ${s.key}`}>
                {isAdmin ? (
                  <Select
                    className="w-full sm:w-72"
                    aria-label={s.label}
                    value={followsMain ? "" : mode}
                    onChange={(e) => e.target.value && setChange({ settingKey: s.key, label: s.label, value: e.target.value, describe: MODE_LABEL[e.target.value as Mode] })}
                  >
                    {followsMain && <option value="">Follows billing emails (not set)</option>}
                    {(["off", "preview", "live"] as Mode[]).map((m) => (
                      <option key={m} value={m}>
                        {MODE_LABEL[m]}
                        {!v.set && m === "preview" && !followsMain ? " (not set)" : ""}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Badge variant={mode === "live" ? "success" : "muted"}>{followsMain ? "Follows billing emails" : MODE_LABEL[mode]}</Badge>
                )}
              </Row>
            );
          })}
        </div>
      )}
      {change && <ChangeSettingDialog {...change} onClose={() => setChange(null)} />}
      {health && <HealthCheckDialog current={hc} onClose={() => setHealth(false)} />}
    </Card>
  );
}

function Row({ label, what, hint, children }: { label: string; what: string; hint: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-[var(--color-muted-foreground)]">{what}</div>
        <div className="num text-xs text-[var(--color-muted-foreground)] opacity-80">{hint}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

/** settings_set for one known key: the server's sentence about what the change does, then confirm. */
export function ChangeSettingDialog({ settingKey, label, value, describe, onClose }: { settingKey: string; label: string; value: unknown; describe: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const flow = usePreviewConfirm(
    (confirm) => api(`/api/v1/settings/${encodeURIComponent(settingKey)}`, { method: "PUT", json: { value, confirm: confirm || undefined } }),
    () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["billing-runs"] });
      toast(`${label}: ${describe}`);
      onClose();
    },
  );
  // Ask the server what the change does as soon as the dialog opens.
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    flow.review();
  }, [flow]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>
          {label}: {describe}?
        </DialogTitle>
        <DialogDescription>Setting {settingKey}. Every change is kept in the audit log.</DialogDescription>
        {flow.reviewing && <Skeleton className="h-10" />}
        {flow.preview && <ServerSays text={plainConfirmMessage(flow.preview.message)} />}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Leave it
          </Button>
          <Button variant="destructive" disabled={!flow.preview || flow.confirming} onClick={flow.confirm}>
            Yes, change it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CardRow {
  id: number;
  gateway: string;
  card_brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  status: string;
  is_default: boolean;
}

const cardText = (c: CardRow) =>
  `${c.card_brand ?? "Card"} ending ${c.last4 ?? "?"}${c.exp_month && c.exp_year ? `, expires ${String(c.exp_month).padStart(2, "0")}/${String(c.exp_year).slice(-2)}` : ""} (${c.gateway === "stripe" ? "Stripe" : c.gateway === "authorize_net" ? "Authorize.net" : c.gateway}, #${c.id})`;

/** billing.health_check: on/off and which of your saved cards it uses, chosen from the contact's cards (no IDs typed). */
export function HealthCheckDialog({ current, onClose }: { current: HealthCheck | undefined; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => api<{ settings: Setting[] }>("/api/v1/settings") });
  const testContacts = settingValue(settings.data?.settings, "billing.stripe_card_test_contacts").value;
  const suggested = Array.isArray(testContacts) && testContacts.length ? String(testContacts[0]) : "";
  const [contact, setContact] = useState<string | null>(null);
  const contactId = Number((contact ?? suggested).trim()) || 0;
  const cards = useQuery({
    queryKey: ["billing-cards", contactId, "all"],
    queryFn: () => api<{ cards: CardRow[] }>(`/api/v1/contacts/${contactId}/cards`).then((r) => r.cards.filter((c) => c.status === "active")),
    enabled: contactId > 0,
  });
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [cardId, setCardId] = useState<number | "">(current?.stripe_payment_method_id ?? "");
  const card = cards.data?.find((c) => c.id === cardId) ?? null;
  const next = healthCheckValue(current, enabled, card);
  const flow = usePreviewConfirm(
    (confirm) => api(`/api/v1/settings/billing.health_check`, { method: "PUT", json: { value: next, confirm: confirm || undefined } }),
    () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      toast(`Monthly card check ${enabled ? "on" : "off"}`);
      onClose();
    },
  );
  const ready = !enabled || !!next.stripe_payment_method_id || !!next.authorize_net_payment_method_id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>{flow.preview ? "Check before saving" : "Monthly $1 card check"}</DialogTitle>
        {!flow.preview ? (
          <>
            <DialogDescription>
              On the 1st of each month at 9 AM Mountain Time the CRM charges $1 to your own saved card and voids it at once (Stripe only holds the $1 and lets it go). It
              proves payments still work. Choose the card from your contact's saved cards.
            </DialogDescription>
            <div className="space-y-3 text-sm">
              <label className="flex items-center gap-2">
                <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Run the check every month
              </label>
              <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
                <div className="space-y-1">
                  <Label htmlFor="hc-contact">Whose card (contact #)</Label>
                  <Input id="hc-contact" inputMode="numeric" value={contact ?? suggested} onChange={(e) => setContact(e.target.value)} placeholder="1000123" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hc-card">Card</Label>
                  <Select id="hc-card" value={cardId} onChange={(e) => setCardId(e.target.value ? Number(e.target.value) : "")} disabled={!cards.data}>
                    <option value="">{contactId ? (cards.isLoading ? "Loading cards…" : "Choose a card…") : "Type the contact number first"}</option>
                    {[...(cards.data ?? [])]
                      .sort((a, b) => Number(b.gateway === "stripe") - Number(a.gateway === "stripe"))
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {cardText(c)}
                        </option>
                      ))}
                  </Select>
                  {cards.data && cards.data.length === 0 && <p className="text-xs text-[var(--color-muted-foreground)]">No saved card on this contact.</p>}
                  {cards.error && <p className="text-xs text-[var(--color-danger-fg)]">Couldn't load the cards: {cards.error.message}</p>}
                </div>
              </div>
              {suggested && <p className="text-xs text-[var(--color-muted-foreground)]">Filled in from your live-test contact (setting billing.stripe_card_test_contacts).</p>}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <ServerSays text={plainConfirmMessage(flow.preview.message)} />
            <Facts
              rows={[
                ["Check", enabled ? "On, monthly" : "Off"],
                ["Stripe card", next.stripe_payment_method_id ? (card?.gateway === "stripe" ? cardText(card) : `#${next.stripe_payment_method_id}`) : "none"],
                ["Authorize.net card", next.authorize_net_payment_method_id ? (card?.gateway === "authorize_net" ? cardText(card) : `#${next.authorize_net_payment_method_id}`) : "none"],
              ]}
            />
          </div>
        )}
        <Problem text={flow.problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={flow.preview ? flow.back : onClose}>
            {flow.preview ? "Back" : "Cancel"}
          </Button>
          {!flow.preview ? (
            <Button disabled={!ready || flow.reviewing} onClick={flow.review}>
              Review
            </Button>
          ) : (
            <Button variant="destructive" disabled={flow.confirming} onClick={flow.confirm}>
              Save
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
